/**
 * @lykbl/dev-tools - Mock users configuration
 * 
 * Different mock users for each app type:
 * - AI-AMP: Full authenticated app users (creator, developer, brand, admin)
 * - Marketing: Public site visitors and prospects
 */

import type { DevModeUser, AiAmpDevRole, MarketingDevRole } from './types';

/**
 * AI-AMP Mock Users
 * For testing the authenticated marketplace app (lykbl.ai)
 */
export const AI_AMP_MOCK_USERS: Record<AiAmpDevRole, DevModeUser> = {
  creator: {
    id: '11111111-1111-1111-1111-111111111111',
    email: 'creator@test.local',
    displayName: 'Test Creator',
    role: 'creator',
    walletBalance: 5000,
    avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=creator',
    description: 'Content creator with unified analytics',
  },
  developer: {
    id: '22222222-2222-2222-2222-222222222222',
    email: 'developer@test.local',
    displayName: 'Test Developer',
    role: 'developer',
    walletBalance: 2000,
    avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=developer',
    description: 'Agent/workflow developer',
  },
  brand: {
    id: '33333333-3333-3333-3333-333333333333',
    email: 'brand@test.local',
    displayName: 'Test Brand Co.',
    role: 'brand',
    walletBalance: 10000,
    avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=brand',
    description: 'Brand/advertiser account',
  },
  admin: {
    id: '44444444-4444-4444-4444-444444444444',
    email: 'admin@test.local',
    displayName: 'Platform Admin',
    role: 'admin',
    walletBalance: 50000,
    avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=admin',
    description: 'Platform administrator',
  },
} as const;

/**
 * Marketing Mock Users
 * For testing the public marketing site (lykbl.com)
 */
export const MARKETING_MOCK_USERS: Record<MarketingDevRole, DevModeUser> = {
  visitor: {
    id: '55555555-5555-5555-5555-555555555555',
    email: 'visitor@test.local',
    displayName: 'Anonymous Visitor',
    role: 'visitor',
    walletBalance: 0,
    avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=visitor',
    description: 'Unauthenticated site visitor',
  },
  'creator-prospect': {
    id: '66666666-6666-6666-6666-666666666666',
    email: 'prospect-creator@test.local',
    displayName: 'Creator Prospect',
    role: 'creator-prospect',
    walletBalance: 0,
    avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=prospect-creator',
    description: 'Potential creator exploring the platform',
  },
  'brand-prospect': {
    id: '77777777-7777-7777-7777-777777777777',
    email: 'prospect-brand@test.local',
    displayName: 'Brand Prospect',
    role: 'brand-prospect',
    walletBalance: 0,
    avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=prospect-brand',
    description: 'Potential brand/advertiser exploring the platform',
  },
  creator: {
    id: '11111111-1111-1111-1111-111111111111',
    email: 'creator@test.local',
    displayName: 'Test Creator',
    role: 'creator',
    walletBalance: 5000,
    avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=creator',
    description: 'Authenticated Creator (cross-site)',
  },
  brand: {
    id: '33333333-3333-3333-3333-333333333333',
    email: 'brand@test.local',
    displayName: 'Test Brand Co.',
    role: 'brand',
    walletBalance: 10000,
    avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=brand',
    description: 'Authenticated Brand (cross-site)',
  },
  admin: {
    id: '44444444-4444-4444-4444-444444444444',
    email: 'admin@test.local',
    displayName: 'Lykbl Admin',
    role: 'admin',
    walletBalance: 50000,
    avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=admin',
    description: 'Platform Super Admin',
  },
} as const;

/**
 * Get mock users for a specific app type
 */
export function getMockUsersForApp(appType: 'ai-amp' | 'marketing') {
  return appType === 'ai-amp' ? AI_AMP_MOCK_USERS : MARKETING_MOCK_USERS;
}

/**
 * Check if a role is valid for a specific app type
 */
export function isValidRoleForApp(
  role: string,
  appType: 'ai-amp' | 'marketing'
): boolean {
  const users = getMockUsersForApp(appType);
  return role in users;
}

